"use client"
import React, { createContext, forwardRef, useMemo, useContext } from "react"
import { useContextSelector } from "use-context-selector"
import {
  ChevronLeft,
  ChevronRight,
  ArrowBackIosNew,
  ArrowForwardIos,
} from "../../icons"
import { classNames } from "../../utils/classNames"
import { CenterAligned } from "../CenterAligned"
import { FlexCenter } from "../FlexCenter"
import { UnstyledButton, UnstyledButtonProps } from "../UnstyledButton"
import { CarouselContext as MainCarouselContext } from "./CarouselProvider"
import {
  arrowButtonBaseVariants,
  arrowButtonContainerVariants,
  carouselArrowButtonVariants,
} from "./styles"

type CarouselNavigationArrowsVariant = "default" | "overlay" | "static"

type CarouselArrowNavButtonProps = UnstyledButtonProps

type CarouselArrowsBaseProps = {
  className?: string
  variant?: CarouselNavigationArrowsVariant
  size?: "md" | "lg"
  children: React.ReactNode
}

const CarouselArrowNavButton = forwardRef<
  HTMLButtonElement,
  CarouselArrowNavButtonProps
>(function CarouselArrowNavButton(
  { className, children, disabled, ...props },
  ref,
) {
  const { variant } = useContext(CarouselArrowsContext)
  return (
    <FlexCenter
      className={classNames(
        arrowButtonContainerVariants({ variant, disabled }),
        className,
      )}
    >
      <UnstyledButton
        className={arrowButtonBaseVariants({ variant })}
        disabled={disabled}
        ref={ref}
        {...props}
      >
        <CenterAligned className="size-full">{children}</CenterAligned>
      </UnstyledButton>
    </FlexCenter>
  )
})

type CarouselNavigationArrowsProps = UnstyledButtonProps

const CarouselPrevious = forwardRef<
  HTMLButtonElement,
  CarouselNavigationArrowsProps
>(function CarouselPrevious({ className, ...props }, ref) {
  const { variant, size } = useContext(CarouselArrowsContext)
  const [scrollPrev, canScrollPrev] = useContextSelector(
    MainCarouselContext,
    ctx => [ctx?.scrollPrev, ctx?.canScrollPrev],
  )

  return (
    <CarouselArrowNavButton
      aria-label="Previous"
      className={classNames(
        carouselArrowButtonVariants({ variant, direction: "previous" }),
        className,
      )}
      disabled={!canScrollPrev}
      ref={ref}
      onClick={scrollPrev}
      {...props}
    >
      {size === "lg" ? (
        <ArrowBackIosNew className="translate-x-[-0.5px]" size={16} />
      ) : (
        <ChevronLeft size={24} />
      )}
    </CarouselArrowNavButton>
  )
})

const CarouselNext = forwardRef<
  HTMLButtonElement,
  CarouselNavigationArrowsProps
>(function CarouselNext({ className, ...props }, ref) {
  const { variant, size } = useContext(CarouselArrowsContext)
  const [scrollNext, canScrollNext] = useContextSelector(
    MainCarouselContext,
    ctx => [ctx?.scrollNext, ctx?.canScrollNext],
  )

  return (
    <CarouselArrowNavButton
      aria-label="Next"
      className={classNames(
        carouselArrowButtonVariants({ variant, direction: "next" }),
        className,
      )}
      disabled={!canScrollNext}
      ref={ref}
      onClick={scrollNext}
      {...props}
    >
      {size === "lg" ? (
        <ArrowForwardIos className="translate-x-[1.5px]" size={16} />
      ) : (
        <ChevronRight size={24} />
      )}
    </CarouselArrowNavButton>
  )
})

const CarouselArrowsContext = createContext<{
  variant: CarouselNavigationArrowsVariant
  size: "md" | "lg"
}>({
  variant: "default",
  size: "md",
})

const CarouselArrowsBase = forwardRef<HTMLDivElement, CarouselArrowsBaseProps>(
  function CarouselArrowsBase(
    { variant = "default", size = "md", children, className },
    ref,
  ) {
    const value = useMemo(() => ({ variant, size }), [variant, size])
    return (
      <CarouselArrowsContext.Provider value={value}>
        {variant === "static" ? (
          <FlexCenter className={classNames("gap-2", className)} ref={ref}>
            {children}
          </FlexCenter>
        ) : (
          children
        )}
      </CarouselArrowsContext.Provider>
    )
  },
)

export const CarouselNavigationArrows = Object.assign(CarouselArrowsBase, {
  Previous: CarouselPrevious,
  Next: CarouselNext,
})
